import { Avatar, HStack, Select, Skeleton, Text, createListCollection, useSelectContext } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

import { type SDGPublic, UtilsService } from '@/client';

interface SdgItem extends SDGPublic {
  src: string;
}

const SelectValueItem = () => {
  const select = useSelectContext();
  const items = select.selectedItems as Array<{ name: string; src: string }>;

  if (!items || items.length === 0) {
    return <Text color="fg.muted">{t('indicator.filter.sdgs.select')}</Text>;
  }

  if (items.length > 1) {
    return (
      <Select.ValueText placeholder={t('indicator.filter.sdgs.select')}>
        {items.length} {t('indicator.selected')}
      </Select.ValueText>
    );
  }

  return (
    <Select.ValueText placeholder={t('indicator.filter.sdgs.select')}>
      <HStack>
        {items.length === 1 && (
          <>
            <Avatar.Root size="2xs" shape="rounded">
              <Avatar.Fallback name={items[0].name} />
              <Avatar.Image src={items[0].src} />
            </Avatar.Root>
            {items[0].name}
          </>
        )}
      </HStack>
    </Select.ValueText>
  );
};

function getSdgsQueryOptions() {
  return {
    queryKey: ['sdgs'],
    queryFn: async () => await UtilsService.readSdgs(),
  };
}

export const SelectSdgs = ({ value, onChange }: { value: string[]; onChange: (value: SDGPublic[]) => void }) => {
  const { i18n } = useTranslation();
  const { data: sdgsData, isLoading } = useQuery({
    ...getSdgsQueryOptions(),
  });

  const sdgsCollection = createListCollection({
    items:
      sdgsData?.data.map((sdg) => ({
        code: sdg.code,
        name: sdg.name,
        src: `/assets/images/sdgs/${i18n.language}/sdg_${sdg.code}.png`,
      })) || [],
    itemToString: (item: SdgItem) => item.name,
    itemToValue: (item: SdgItem) => item.code,
  });

  return (
    <>
      {isLoading ? (
        <Skeleton height="8" />
      ) : (
        <Select.Root
          multiple
          collection={sdgsCollection}
          size="sm"
          value={value}
          onValueChange={(e) => onChange(sdgsCollection.items.filter((el) => e.value.includes(el.code)))}
          positioning={{ sameWidth: true }}
        >
          <Select.HiddenSelect />
          <Select.Control>
            <Select.Trigger>
              <SelectValueItem />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.ClearTrigger />
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              {sdgsCollection.items.map((sdg: SdgItem) => (
                <Select.Item item={sdg} key={sdg.code} justifyContent="space-between">
                  <HStack>
                    <Avatar.Root size="2xs" shape="rounded">
                      <Avatar.Fallback name={sdg.name} />
                      <Avatar.Image src={sdg.src} />
                    </Avatar.Root>
                    {sdg.name}
                  </HStack>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      )}
    </>
  );
};

export default SelectSdgs;
